import { Component } from '@theme/component';
import { ThemeEvents, VariantUpdateEvent, ZoomMediaSelectedEvent } from '@theme/events';
import { isOnMainProductPage } from '@theme/utilities';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  /** @type {AbortController | undefined} */
  #controller;

  /** @type {IntersectionObserver | null} */
  #videoObserver = null;

  connectedCallback() {
    super.connectedCallback();

    this.#controller = new AbortController();
    const { signal } = this.#controller;

    if (isOnMainProductPage(this)) {
      document.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    } else {
      const target = this.closest('.shopify-section, dialog');
      target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    }
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });

    if (this.videoAutoplayOnSlide) {
      this.#initVideoObserver();
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller?.abort();
    this.#controller = undefined;
    this.#videoObserver?.disconnect();
  }

  /**
   * Handles a variant update event by replacing the current media gallery with a new one.
   *
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #handleVariantUpdate = (event) => {
    // Check productId to avoid updates from other products (e.g., recommended products)
    if (event.detail.data.newProduct) {
      this.dataset.productId = String(event.detail.data.newProduct.id);
    } else if (event.detail.data.productId && this.dataset.productId && event.detail.data.productId !== this.dataset.productId) {
      return;
    }

    const source = event.detail.data.html;

    if (!source) return;
    const newMediaGallery = source.querySelector('media-gallery');

    if (!newMediaGallery) return;

    this.replaceWith(newMediaGallery);
  };

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }

  get videoAutoplayOnSlide() {
    return this.hasAttribute('data-video-autoplay-on-slide');
  }

  #initVideoObserver() {
    const deferredMediaElements = this.querySelectorAll('deferred-media');
    if (!deferredMediaElements.length) return;

    const isCarousel = this.presentation === 'carousel';
    const root = isCarousel ? this.refs.slideshow?.refs.scroller : null;

    this.#videoObserver = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          /** @type {any} */
          const deferredMedia = entry.target;

          if (entry.isIntersecting) {
            const isLoaded = deferredMedia.hasAttribute('data-media-loaded');
            if (isLoaded) {
              deferredMedia.playMedia?.();
            } else {
              deferredMedia.showDeferredMedia?.();
            }
          } else {
            deferredMedia.pauseMedia?.();
          }
        }
      },
      { root, threshold: 0.8 }
    );

    for (const media of deferredMediaElements) {
      this.#videoObserver.observe(media);
    }
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
